// website/src/layout/AppLayout.tsx

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Menu, ChevronRight } from "lucide-react";
import Sidebar, { APP_NAV_ITEMS } from "../components/app/Sidebar";
import { useAuth } from "../hooks/useAuth";
import { getProfile, type UserProfile } from "../lib/api";
import { useT } from "../i18n/useT";
import "../styles/app-glass.css";

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { token } = useAuth();
  const t = useT();

  const [profile, setProfile] = useState<UserProfile | null>(null);

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      const authToken = token || localStorage.getItem("quiz_token");
      if (!authToken) {
        return;
      }
      try {
        const data = await getProfile(authToken);
        setProfile(data);
      } catch (err) {
        console.warn("Failed to fetch profile for pause state:", err);
      }
    };
    fetchProfile();
  }, [token]);

  // 🔥 Listen for profile updates from AppDashboard
  useEffect(() => {
    const handleProfileUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<UserProfile>;
      setProfile(customEvent.detail);
    };

    window.addEventListener(
      "profileUpdated",
      handleProfileUpdate as EventListener,
    );
    return () => {
      window.removeEventListener(
        "profileUpdated",
        handleProfileUpdate as EventListener,
      );
    };
  }, []);

  const isSubscriptionPaused = useMemo(() => {
    if (!profile) return false;
    return profile.has_used_pause && !profile.access_started_at;
  }, [profile]);

  // Expose the sticky header height so nested pages can offset their own
  // sticky elements below it (avoids two sticky bars overlapping).
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useLayoutEffect(() => {
    if (!headerRef.current) {
      setHeaderHeight(0);
      return;
    }
    const el = headerRef.current;
    const update = () => setHeaderHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const sectionTitle = useMemo(() => {
    const routeTitles = [
      ...APP_NAV_ITEMS.map((item) => ({
        to: item.to,
        label: t.nav[item.labelKey],
      })),
      { to: "/app/leercoach/oefenen", label: t.nav.aiCoach },
      { to: "/app/downloads", label: t.nav.downloads },
      { to: "/app/bestellingen", label: t.nav.orders },
      { to: "/app/uitslag", label: t.nav.result },
    ].sort((a, b) => b.to.length - a.to.length);

    return (
      routeTitles.find((item) => location.pathname.startsWith(item.to))
        ?.label ?? t.nav.dashboard
    );
  }, [location.pathname, t]);

  return (
    <div className="app-shell flex min-h-screen">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onToggle={() => setSidebarOpen((prev) => !prev)}
        isPaused={isSubscriptionPaused}
      />

      <div
        className="app-workspace flex min-h-screen min-w-0 flex-1 flex-col lg:ml-72"
        style={{
          ["--app-header-h" as string]: `calc(${headerHeight}px + var(--app-header-gap, 0px))`,
        }}
      >
        <header
          ref={headerRef}
          className="app-toolbar sticky top-0 z-30 flex min-w-0 items-center justify-between gap-3 px-4 py-4 lg:px-10"
        >
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <button
              type="button"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-700 transition hover:border-neutral-300 hover:text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:border-neutral-500 dark:hover:text-white lg:hidden"
              onClick={() => setSidebarOpen((prev) => !prev)}
              aria-label={t.nav.toggleNavigation}
              aria-expanded={sidebarOpen}
              aria-controls="app-navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="app-breadcrumb min-w-0 flex items-center gap-2 text-sm">
              <Link to="/app/dashboard" className="hidden shrink-0 sm:block">
                Theorie101
              </Link>
              <ChevronRight
                className="hidden h-3.5 w-3.5 shrink-0 sm:block"
                aria-hidden="true"
              />
              <span
                className="truncate font-medium"
                title={t.nav.section(sectionTitle)}
              >
                {sectionTitle}
              </span>
            </div>
          </div>
        </header>

        <main className="app-content min-w-0 flex-1 px-4 pb-6 pt-8 sm:px-6 lg:px-10">
          <div className="mx-auto flex w-full min-w-0 max-w-7xl flex-col gap-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
